import { useCallback, useEffect, useRef, useState } from 'react';

// Use the same media element so time, mute and the single-player lock survive.
// iPhone's native player is the fallback when element fullscreen is unavailable.
export default function useVideoFullscreen(root, element, enabled) {
  const [fullscreen, setFullscreen] = useState(false);
  const [error, setError] = useState(false);
  const nativeFullscreen = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    if (!enabled) return undefined;
    mounted.current = true;
    const node = element.current, container = root.current;
    const changed = () => setFullscreen(document.fullscreenElement === container);
    const entered = () => { nativeFullscreen.current = true; setFullscreen(true); };
    const exited = () => { nativeFullscreen.current = false; setFullscreen(false); };
    document.addEventListener('fullscreenchange', changed);
    node?.addEventListener('webkitbeginfullscreen', entered);
    node?.addEventListener('webkitendfullscreen', exited);
    return () => {
      mounted.current = false;
      document.removeEventListener('fullscreenchange', changed);
      node?.removeEventListener('webkitbeginfullscreen', entered);
      node?.removeEventListener('webkitendfullscreen', exited);
      if (document.fullscreenElement === container) void document.exitFullscreen?.().catch(() => {});
      if (nativeFullscreen.current) { try { node?.webkitExitFullscreen?.(); } catch { /* Already dismissed by the OS. */ } }
    };
  }, [root, element, enabled]);
  const toggleFullscreen = useCallback(async () => {
    setError(false);
    const node = element.current, container = root.current;
    try {
      if (document.fullscreenElement === container) await document.exitFullscreen();
      else if (nativeFullscreen.current) node.webkitExitFullscreen();
      else if (container?.requestFullscreen && document.fullscreenEnabled !== false) await container.requestFullscreen();
      else if (node?.webkitEnterFullscreen) node.webkitEnterFullscreen();
      else throw new Error('Fullscreen unavailable');
    } catch { if (mounted.current) setError(true); }
  }, [root, element]);
  return { fullscreen, toggleFullscreen, fullscreenError: error, nativeFullscreen };
}
